import { NavLink, Link } from "react-router-dom";
import {
  House,
  PaperPlaneTilt,
  Gear,
  Archive,
  Buildings,
  FileText,
  Ticket,
  SignIn,
  SignOut,
  UserCircle,
  Wrench,
  ChartLineUp,
  SlidersHorizontal,
} from "@phosphor-icons/react";
import { POLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLE_ICONS = {
  comptabilite: "Cb",
  expedition: "Ex",
  demontage: "Dm",
  expertise: "Et",
  sav: "Sv",
  magasin: "Mg",
  call_center: "Cc",
  stock: "St",
  administratif: "Ad",
};

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-all duration-150 ${
    isActive
      ? "border-l-zinc-900 bg-zinc-100 text-zinc-900 font-medium"
      : "border-l-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
  }`;

export function SidebarContent({ stats, onNavigate }) {
  // Poles come from stats (backend-driven, honours DB + active filter, order preserved)
  const poleList = (stats || []).map((s) => ({ id: s.id, label: s.name || POLE_LABELS[s.id] }));
  const handleNav = () => onNavigate && onNavigate();
  const { user, logout } = useAuth() || {};

  return (
    <>
      <div className="px-6 py-6 border-b border-zinc-200">
        <Link to="/dashboard" onClick={handleNav} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="logo-home-link">
          <img src="/logo-ecocasse.png" alt="Ecocasse" className="w-9 h-9 object-contain" />
          <div>
            <div className="font-heading font-semibold text-base leading-none">Portail d'activité</div>
            <div className="label-tiny text-zinc-500 mt-1">Ecocasse</div>
          </div>
        </Link>
      </div>

      <nav className="py-4 flex-1 overflow-y-auto">
        <div className="label-tiny text-zinc-400 px-4 mb-2">Navigation</div>
        <NavLink to="/dashboard" end onClick={handleNav} className={navLinkClass} data-testid="nav-dashboard">
          <House size={18} weight="duotone" />
          <span>Tableau de bord</span>
        </NavLink>
        <NavLink to="/envoyer" onClick={handleNav} className={navLinkClass} data-testid="nav-envoyer">
          <PaperPlaneTilt size={18} weight="duotone" />
          <span>Envoyer un PDF</span>
        </NavLink>
        <NavLink to="/commandes" onClick={handleNav} className={navLinkClass} data-testid="nav-commandes">
          <Wrench size={18} weight="duotone" />
          <span>Commandes</span>
        </NavLink>
        <NavLink to="/recherche" onClick={handleNav} className={navLinkClass} data-testid="nav-recherche">
          <Archive size={18} weight="duotone" />
          <span>Recherche / Archives</span>
        </NavLink>
        <NavLink to="/tickets" onClick={handleNav} className={navLinkClass} data-testid="nav-tickets">
          <Ticket size={18} weight="duotone" />
          <span>Tickets clients</span>
        </NavLink>

        <NavLink to="/performance" onClick={handleNav} className={navLinkClass} data-testid="nav-performance">
          <ChartLineUp size={18} weight="duotone" />
          <span className="flex-1">Performance</span>
        </NavLink>
        {user?.role === "admin" && (
          <NavLink to="/performance/admin" onClick={handleNav} className={navLinkClass} data-testid="nav-performance-admin">
            <SlidersHorizontal size={18} weight="duotone" />
            <span className="flex-1">Admin Performance</span>
          </NavLink>
        )}

        <div className="label-tiny text-zinc-400 px-4 mb-2 mt-6">Pôles</div>
        {poleList.map(({ id, label }) => {
          const s = stats?.find((x) => x.id === id);
          const red = (s?.unassigned || 0) + (s?.tickets_unassigned || 0);
          const icon = POLE_ICONS[id] || (label || id).slice(0, 2).toUpperCase();
          return (
            <NavLink
              key={id}
              to={`/pole/${id}`}
              onClick={handleNav}
              className={navLinkClass}
              data-testid={`nav-pole-${id}`}
            >
              <span className="w-6 h-6 border border-zinc-300 flex items-center justify-center text-[10px] font-mono font-semibold text-zinc-700">
                {icon}
              </span>
              <span className="flex-1 truncate">{label}</span>
              {red > 0 && (
                <span
                  className="bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px] text-center pulse-red"
                  data-testid={`nav-pole-${id}-red-count`}
                >
                  {red}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-zinc-200 p-4">
        <NavLink to="/admin" onClick={handleNav} className={navLinkClass} data-testid="nav-admin">
          <Gear size={18} weight="duotone" />
          <span>Back office Admin</span>
        </NavLink>
        {user ? (
          <div className="mt-3 px-3 py-2 bg-zinc-50 border border-zinc-200" data-testid="sidebar-user-block">
            <div className="flex items-center gap-2 mb-2">
              <UserCircle size={22} weight="duotone" className="text-zinc-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{user.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{user.role}</div>
              </div>
            </div>
            <button
              onClick={() => { logout(); handleNav(); }}
              className="w-full text-[11px] font-medium text-red-700 hover:bg-red-50 border border-red-200 px-2 py-1.5 inline-flex items-center justify-center gap-1"
              data-testid="sidebar-logout"
            >
              <SignOut size={11} weight="bold" /> Se déconnecter
            </button>
          </div>
        ) : (
          <NavLink to="/login" onClick={handleNav} className={navLinkClass} data-testid="nav-login">
            <SignIn size={18} weight="duotone" />
            <span>Connexion back-office</span>
          </NavLink>
        )}
        <div className="mt-4 px-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Buildings size={14} weight="duotone" />
            <span>{user ? `Rôle : ${user.role}` : "Mode public"}</span>
          </div>
          <Link
            to="/portail"
            onClick={handleNav}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-900 underline"
            data-testid="sidebar-portal-link"
          >
            ← Retour au portail client
          </Link>
        </div>
      </div>
    </>
  );
}

export default function Sidebar({ stats }) {
  return (
    <aside
      className="hidden lg:flex w-64 flex-col bg-white border-r border-zinc-200 h-screen sticky top-0"
      data-testid="sidebar"
    >
      <SidebarContent stats={stats} />
    </aside>
  );
}
